import {  useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, MessageCircle } from 'lucide-react';
import { colors } from '../../styles/designSystem';
import posthog from 'posthog-js';

import { DialogForm } from '../ui/DialogForm';


const FAQ = () => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [variant, setVariant] = useState('control');
  
  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formData = new FormData(e.target);
      const response = await fetch('https://formspree.io/f/xovvpnyb', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        setShowThankYou(true);
        e.target.reset();
        // Track successful demo request
        posthog.capture('demo_request', {
          variant: variant
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleButtonClick = (buttonType) => {
    posthog.capture('button_click', {
      button_type: buttonType
    });
  
    if (window.gtag) {
      window.gtag('event', 'button_click', {
        'event_category': 'engagement',
        'event_label': buttonType
      });
    }
  };

  const faqs = [
    {
      question: t('faq.questions.handling_complex_inquiries.question'),
      answer: t('faq.questions.handling_complex_inquiries.answer')
    },
    {
      question: t('faq.questions.supported_languages.question'),
      answer: t('faq.questions.supported_languages.answer')
    },
    {
      question: t('faq.questions.getting_started.question'),
      answer: t('faq.questions.getting_started.answer')
    },
    {
      question: t('faq.questions.data_security_privacy.question'),
      answer: t('faq.questions.data_security_privacy.answer')
    },
    {
      question: t('faq.questions.billing.question'),
      answer: t('faq.questions.billing.answer')
    }
  ];

  return (
    <section className="py-6 relative overflow-hidden">
      {/* Background with gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-gray-100" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className={`${colors.text.heading} ${colors.primary.text} mb-6`}>
            {t('faq.heading')}
          </h2>
          <p className={`${colors.text.body} max-w-2xl mx-auto`}>
            {t('faq.description')}
          </p>
        </div>

        {/* FAQ Items */}
        <div className="max-w-3xl mx-auto">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="mb-4"
            >
              <div className="relative group">
                {/* Gradient border effect */}
                <div className={`absolute inset-0 ${colors.primary.light} rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                
                <div className={`relative ${colors.container.base} ${colors.container.hover} p-6`}>
                  <button
                    className="w-full flex justify-between items-center"
                    onClick={() => setActiveIndex(activeIndex === index ? null : index)}
                  >
                    <span className="font-semibold text-left">{faq.question}</span>
                    {activeIndex === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                  
                  {activeIndex === index && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className={colors.text.body}>{faq.answer}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Support CTA */}
        <div className="mt-8 text-center">
          <div className={`${colors.container.base} inline-block p-6`}>
            <div className="flex items-center justify-center space-x-4">
              <div className="p-3 rounded-lg bg-gradient-to-r from-violet-500/10 to-indigo-500/10">
                <MessageCircle className="w-6 h-6 text-violet-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold">{t('faq.support_cta.title')}</p>
                <p className={colors.text.subtitle}>{t('faq.support_cta.subtitle')}</p>
              </div>
              <DialogForm
                triggerText={t('App.book_demo')}
                title={t('App.book_demo')}
                description="Schedule a personalized demo with our team"
                onSubmit={handleDemoSubmit}
                onClick={() => handleButtonClick('demo_button')}

              />
                <DialogForm
                  triggerText={t('App.get_free_trial')}
                  title={t('App.get_free_trial')}
                  description="Start your free trial today"
                  onSubmit={handleDemoSubmit}
                  onClick={() => handleButtonClick('trial_button')}
                  buttonStyle="black"
  />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
